import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { ArrowUpRight, CalendarCheck, Check, Clock, Loader2, Mail, MapPin, Phone, Send } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { MaskedLine, FadeUp } from "@/components/Reveal";
import { SchedulerEmbed } from "@/components/SchedulerEmbed";
import { trackLeadConversion } from "@/lib/googleAds";
import { CONTACT, SCHEDULER_URL, isRealSchedulerUrl, usePageTitle } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CLIENT_TYPES = ["Small business", "Tribal government", "Nonprofit", "Individual", "Other"];
const SERVICES = ["Accounting", "Audit preparation", "Bookkeeping", "Not sure yet"];
const TIME_WINDOWS = ["Morning (9 AM - 12 PM PT)", "Afternoon (12 - 3 PM PT)", "Late afternoon (3 - 5 PM PT)"];

const Field = ({ label, htmlFor, children }) => (
  <div className="space-y-2">
    <Label htmlFor={htmlFor} className="text-xs font-semibold uppercase tracking-[0.15em] text-zinc-500">
      {label}
    </Label>
    {children}
  </div>
);

const inputCls =
  "h-12 rounded-xl border-black/15 bg-white text-base focus-visible:ring-pine";

function ConsultationForm() {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", organization: "", client_type: "", service: "", message: "",
  });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await axios.post(`${API}/leads`, { ...form, phone: form.phone || null, organization: form.organization || null });
      trackLeadConversion(res.data?.lead_id);
      setDone(true);
      toast.success("Request received — we will be in touch within one business day.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  if (done) {
    return (
      <div data-testid="consultation-success" className="flex flex-col items-start gap-4 py-10">
        <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-pine/10">
          <Check className="h-6 w-6 text-pine" />
        </span>
        <h3 className="font-serif text-2xl tracking-tight text-ink">Request received.</h3>
        <p className="max-w-md text-sm leading-relaxed text-zinc-600">
          Thank you, {form.name.split(" ")[0]}. A confirmation is on its way to your inbox, and
          our team will respond within one business day.
        </p>
        <Button
          variant="outline"
          data-testid="consultation-another-button"
          onClick={() => { setDone(false); setForm({ name: "", email: "", phone: "", organization: "", client_type: "", service: "", message: "" }); }}
          className="rounded-full"
        >
          Send another request
        </Button>
      </div>
    );
  }

  return (
    <form data-testid="consultation-form" onSubmit={submit} className="space-y-5">
      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="Full name *" htmlFor="lead-name">
          <Input id="lead-name" data-testid="consultation-name-input" required minLength={2} value={form.name} onChange={set("name")} placeholder="Jordan Miller" className={inputCls} />
        </Field>
        <Field label="Email *" htmlFor="lead-email">
          <Input id="lead-email" data-testid="consultation-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="you@organization.org" className={inputCls} />
        </Field>
      </div>
      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="Phone" htmlFor="lead-phone">
          <Input id="lead-phone" data-testid="consultation-phone-input" type="tel" value={form.phone} onChange={set("phone")} placeholder="(206) 555-0100" className={inputCls} />
        </Field>
        <Field label="Organization" htmlFor="lead-org">
          <Input id="lead-org" data-testid="consultation-organization-input" value={form.organization} onChange={set("organization")} placeholder="Business or tribe name" className={inputCls} />
        </Field>
      </div>
      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="I am a *" htmlFor="lead-type">
          <Select required value={form.client_type} onValueChange={set("client_type")}>
            <SelectTrigger id="lead-type" data-testid="consultation-client-type-select" className={inputCls}>
              <SelectValue placeholder="Select one" />
            </SelectTrigger>
            <SelectContent>
              {CLIENT_TYPES.map((t) => (
                <SelectItem key={t} value={t} data-testid={`client-type-${t.toLowerCase().replace(/\s+/g, "-")}`}>{t}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Service interest *" htmlFor="lead-service">
          <Select required value={form.service} onValueChange={set("service")}>
            <SelectTrigger id="lead-service" data-testid="consultation-service-select" className={inputCls}>
              <SelectValue placeholder="Select one" />
            </SelectTrigger>
            <SelectContent>
              {SERVICES.map((s) => (
                <SelectItem key={s} value={s} data-testid={`service-${s.toLowerCase().replace(/\s+/g, "-")}`}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
      </div>
      <Field label="How can we help? *" htmlFor="lead-message">
        <Textarea
          id="lead-message"
          data-testid="consultation-message-input"
          required
          minLength={10}
          rows={5}
          value={form.message}
          onChange={set("message")}
          placeholder="Tell us a little about your organization and what you need — for example, monthly bookkeeping, an upcoming audit, or a QuickBooks clean-up."
          className="rounded-xl border-black/15 bg-white text-base focus-visible:ring-pine"
        />
      </Field>
      <Button
        type="submit"
        data-testid="consultation-submit-button"
        disabled={sending}
        className="group h-13 w-full rounded-full bg-pine px-7 py-6 text-sm font-semibold text-white transition-colors duration-300 hover:bg-clay"
      >
        {sending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />}
        {sending ? "Sending…" : "Send consultation request"}
      </Button>
      <p className="text-xs leading-relaxed text-zinc-500">
        We respond within one business day. Your details are handled per our{" "}
        <Link to="/privacy" data-testid="consultation-privacy-link" className="font-medium text-pine underline underline-offset-2 transition-colors hover:text-clay">privacy policy</Link>{" "}
        and never shared.
      </p>
    </form>
  );
}

function BookingForm() {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", service: "", preferred_date: "", time_window: "", notes: "",
  });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await axios.post(`${API}/bookings`, { ...form, phone: form.phone || null, notes: form.notes || null });
      trackLeadConversion(res.data?.booking_id);
      setDone(true);
      toast.success("Booking request received — we will confirm your time shortly.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  if (done) {
    return (
      <div data-testid="booking-success" className="flex flex-col items-start gap-4 py-10">
        <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-pine/10">
          <CalendarCheck className="h-6 w-6 text-pine" />
        </span>
        <h3 className="font-serif text-2xl tracking-tight text-ink">Appointment requested.</h3>
        <p className="max-w-md text-sm leading-relaxed text-zinc-600">
          We have your request for {form.preferred_date} ({form.time_window}). You will receive
          a confirmation email shortly, and we will finalize the exact time within one business day.
        </p>
        <Button
          variant="outline"
          data-testid="booking-another-button"
          onClick={() => { setDone(false); setForm({ name: "", email: "", phone: "", service: "", preferred_date: "", time_window: "", notes: "" }); }}
          className="rounded-full"
        >
          Request another time
        </Button>
      </div>
    );
  }

  return (
    <form data-testid="booking-form" onSubmit={submit} className="space-y-5">
      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="Full name *" htmlFor="book-name">
          <Input id="book-name" data-testid="booking-name-input" required minLength={2} value={form.name} onChange={set("name")} placeholder="Jordan Miller" className={inputCls} />
        </Field>
        <Field label="Email *" htmlFor="book-email">
          <Input id="book-email" data-testid="booking-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="you@organization.org" className={inputCls} />
        </Field>
      </div>
      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="Phone" htmlFor="book-phone">
          <Input id="book-phone" data-testid="booking-phone-input" type="tel" value={form.phone} onChange={set("phone")} placeholder="(206) 555-0100" className={inputCls} />
        </Field>
        <Field label="Service *" htmlFor="book-service">
          <Select required value={form.service} onValueChange={set("service")}>
            <SelectTrigger id="book-service" data-testid="booking-service-select" className={inputCls}>
              <SelectValue placeholder="Select one" />
            </SelectTrigger>
            <SelectContent>
              {SERVICES.map((s) => (
                <SelectItem key={s} value={s} data-testid={`booking-service-${s.toLowerCase().replace(/\s+/g, "-")}`}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
      </div>
      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="Preferred date *" htmlFor="book-date">
          <Input id="book-date" data-testid="booking-date-input" required type="date" value={form.preferred_date} onChange={set("preferred_date")} className={inputCls} />
        </Field>
        <Field label="Time window *" htmlFor="book-window">
          <Select required value={form.time_window} onValueChange={set("time_window")}>
            <SelectTrigger id="book-window" data-testid="booking-time-window-select" className={inputCls}>
              <SelectValue placeholder="Select one" />
            </SelectTrigger>
            <SelectContent>
              {TIME_WINDOWS.map((t) => (
                <SelectItem key={t} value={t} data-testid={`time-window-${t.split(" ")[0].toLowerCase()}`}>{t}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
      </div>
      <Field label="Anything we should know?" htmlFor="book-notes">
        <Textarea
          id="book-notes"
          data-testid="booking-notes-input"
          rows={4}
          value={form.notes}
          onChange={set("notes")}
          placeholder="Optional — context for the meeting, deadlines, or topics to cover."
          className="rounded-xl border-black/15 bg-white text-base focus-visible:ring-pine"
        />
      </Field>
      <Button
        type="submit"
        data-testid="booking-submit-button"
        disabled={sending}
        className="group w-full rounded-full bg-pine px-7 py-6 text-sm font-semibold text-white transition-colors duration-300 hover:bg-clay"
      >
        {sending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CalendarCheck className="mr-2 h-4 w-4 transition-transform duration-300 group-hover:scale-110" />}
        {sending ? "Requesting…" : "Request appointment"}
      </Button>
      <p className="text-xs leading-relaxed text-zinc-500">
        This sends a request — we confirm the exact time by email. Prefer to self-schedule? Use
        the scheduling link on this page.
      </p>
    </form>
  );
}

export default function Contact() {
  usePageTitle("Contact — Request a Consultation or Book an Appointment | CalCor Solutions");
  const { hash } = useLocation();
  const defaultTab = hash === "#booking" ? "booking" : "consultation";

  return (
    <>
      <section data-testid="contact-hero" className="pt-36 pb-14 sm:pt-44">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <MaskedLine delay={0.15} className="text-xs font-semibold uppercase tracking-[0.25em] text-pine">
            Contact
          </MaskedLine>
          <h1 className="mt-6 font-serif text-5xl font-medium leading-[1.05] tracking-tighter text-ink sm:text-6xl lg:text-7xl">
            <MaskedLine delay={0.3}>Start the</MaskedLine>
            <MaskedLine delay={0.45}>
              <em className="text-pine">conversation.</em>
            </MaskedLine>
          </h1>
          <FadeUp delay={0.6}>
            <p className="mt-8 max-w-2xl text-base leading-relaxed text-zinc-600 sm:text-lg">
              Request a consultation, book an appointment, or simply say hello. We reply within
              one business day — usually sooner.
            </p>
          </FadeUp>
        </div>
      </section>

      <section data-testid="contact-body" className="pb-24 lg:pb-32">
        <div className="mx-auto grid max-w-7xl gap-10 px-6 lg:grid-cols-12 lg:px-10">
          {/* ————— Details column ————— */}
          <div className="space-y-5 lg:col-span-5">
            <FadeUp>
              <div className="rounded-3xl border border-black/10 bg-white p-9">
                <h2 className="font-serif text-2xl tracking-tight text-ink">Reach us directly</h2>
                <ul className="mt-7 space-y-5">
                  <li className="flex items-start gap-4">
                    <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-pine/10">
                      <Phone className="h-4 w-4 text-pine" />
                    </span>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.15em] text-zinc-500">Phone</p>
                      <a data-testid="contact-phone-link" href={`tel:${CONTACT.phone.replace(/[^0-9+]/g, "")}`} className="mt-1 block text-sm font-semibold text-ink transition-colors hover:text-clay">
                        {CONTACT.phone}
                      </a>
                    </div>
                  </li>
                  <li className="flex items-start gap-4">
                    <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-pine/10">
                      <Mail className="h-4 w-4 text-pine" />
                    </span>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.15em] text-zinc-500">Email</p>
                      <a data-testid="contact-email-link" href={`mailto:${CONTACT.email}`} className="mt-1 block text-sm font-semibold text-ink transition-colors hover:text-clay">
                        {CONTACT.email}
                      </a>
                    </div>
                  </li>
                  <li className="flex items-start gap-4">
                    <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-pine/10">
                      <MapPin className="h-4 w-4 text-pine" />
                    </span>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.15em] text-zinc-500">Based in</p>
                      <p data-testid="contact-location" className="mt-1 text-sm font-semibold text-ink">
                        {CONTACT.location} — serving clients remotely, anywhere
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start gap-4">
                    <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-pine/10">
                      <Clock className="h-4 w-4 text-pine" />
                    </span>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.15em] text-zinc-500">Hours</p>
                      <p data-testid="contact-hours" className="mt-1 text-sm font-semibold text-ink">{CONTACT.hours}</p>
                    </div>
                  </li>
                </ul>
              </div>
            </FadeUp>

            <FadeUp delay={0.1}>
              <div className="rounded-3xl border border-black/10 bg-pine p-9 text-paper">
                <h2 className="font-serif text-2xl tracking-tight">Prefer to self-schedule?</h2>
                {isRealSchedulerUrl(SCHEDULER_URL) ? (
                  <>
                    <p className="mt-3 text-sm leading-relaxed text-paper/70">
                      Pick a time that works for you directly on our scheduling calendar — or scroll
                      down to book without leaving this page.
                    </p>
                    <a
                      href={SCHEDULER_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="scheduler-link-button"
                      className="group mt-6 inline-flex items-center gap-2 rounded-full bg-white px-6 py-3.5 text-sm font-semibold text-pine transition-colors duration-300 hover:bg-clay hover:text-white"
                    >
                      Open scheduling calendar
                      <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                    </a>
                  </>
                ) : (
                  <>
                    <p className="mt-3 text-sm leading-relaxed text-paper/70">
                      Instant scheduling is being connected. In the meantime, request an appointment
                      with the booking form and we will confirm within one business day.
                    </p>
                    <span
                      data-testid="scheduler-coming-soon-note"
                      className="mt-6 inline-flex items-center gap-2 rounded-full border border-white/30 px-6 py-3.5 text-sm font-semibold text-paper/70"
                    >
                      Live calendar coming soon
                    </span>
                  </>
                )}
              </div>
            </FadeUp>

            <FadeUp delay={0.15}>
              <div className="flex items-center justify-between gap-4 rounded-3xl border border-black/10 bg-white p-7">
                <div>
                  <p className="font-serif text-lg tracking-tight text-ink">Existing client?</p>
                  <p className="mt-1 text-sm text-zinc-600">Access your secure client portal.</p>
                </div>
                <Link
                  to="/portal"
                  data-testid="contact-portal-link"
                  className="group inline-flex shrink-0 items-center gap-1.5 rounded-full border border-black/15 px-5 py-2.5 text-sm font-semibold text-ink transition-colors duration-300 hover:border-pine hover:text-pine"
                >
                  Portal
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                </Link>
              </div>
            </FadeUp>
          </div>

          {/* ————— Forms column ————— */}
          <FadeUp delay={0.1} className="lg:col-span-7">
            <div className="rounded-3xl border border-black/10 bg-white p-8 sm:p-10">
              <Tabs defaultValue={defaultTab} data-testid="contact-tabs">
                <TabsList className="grid w-full grid-cols-2 rounded-full bg-paper p-1">
                  <TabsTrigger
                    value="consultation"
                    data-testid="tab-consultation"
                    className="rounded-full text-sm font-semibold data-[state=active]:bg-pine data-[state=active]:text-white"
                  >
                    Request a consultation
                  </TabsTrigger>
                  <TabsTrigger
                    value="booking"
                    data-testid="tab-booking"
                    className="rounded-full text-sm font-semibold data-[state=active]:bg-pine data-[state=active]:text-white"
                  >
                    Book an appointment
                  </TabsTrigger>
                </TabsList>
                <TabsContent value="consultation" className="pt-8">
                  <ConsultationForm />
                </TabsContent>
                <TabsContent value="booking" className="pt-8">
                  <BookingForm />
                </TabsContent>
              </Tabs>
            </div>
          </FadeUp>
        </div>
      </section>

      <SchedulerEmbed />
    </>
  );
}
